// @flow
import type { StoreState } from 'reducers';
import type { RequestStates } from 'types/redux';
import type { RouterProps } from 'types/models-config';
import type { UserState } from 'reducers/user';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import _ from 'lodash';
import DocumentTitle from 'react-document-title';
import { Header, Button, Segment, Grid } from 'semantic-grab-ui/src';
import scribe from '@grab-x/scribe-web-sdk';

import ProgressiveContainer from 'components/container/ProgressiveContainer';
import { createEntity, setActiveEntity, getEntity } from 'actions/common';
import Toastr from 'utils/toastr-util';
import { isRequestsPending, hasJustSuccess } from 'utils/request-state-utils';
import { hasPermission } from 'utils/user-utils';
import { parseUCDSyncErrMsg } from 'omega-rtc/utils/ucd-utils';
import { scrollToElement } from 'omega-rtc/utils/trident-node-utils';
import ScribeEventName from '../../constants/scribe';
import { processCampaignData, sendOpenEvent } from '../../utils/scribe';

type Props = {
  newlyCreatedID: number,
  userPermissions: Object,
  user: UserState,
  permissionKey: string,
  createEntity: Function,
  createEntityRequest: RequestStates,
  defaultEntity?: (initialValues?: Object, routerProps?: RouterProps) => Object,
  router: Object,
  disableOnSuccessLink: Boolean,
  routerProps: RouterProps,
  localeSelector: string,
  allowActivation?: boolean,
  allowDuplication?: boolean,
  hasCountryIDPath?: boolean,
  nonDuplicateFields?: Array<string>,
  modelName: string,
  webPath: string,
  apiPath: string,
  stateKey: string,
  views: Object,
  idField: string,
  setActiveEntity: Function,
  entity: Object,
  getEntity: Function,
  entityIDToDuplicate: number,
  isStayOnCurrentPage: boolean,
  modifyModelToDuplicateFunc: Function,
  ucdErrMessage: string,
};

class NewPage extends Component {
  props: Props;

  static contextTypes = {
    router: PropTypes.object,
  };

  componentDidMount() {
    const {
      modelName,
      defaultEntity = () => ({}),
      allowDuplication,
      entityIDToDuplicate,
      localeSelector,
      nonDuplicateFields,
      routerProps,
      user,
    } = this.props;

    if (allowDuplication && entityIDToDuplicate) {
      this.props.getEntity(
        modelName,
        entityIDToDuplicate,
        _.get(this.props.routerProps, 'location.query'),
      );
      Toastr.success(
        i18n.t('flash.actions.duplicate_success', {
          model_name: i18n.t(`models.${localeSelector}`),
        }),
      );
      Toastr.info(
        _.map(nonDuplicateFields, fieldName =>
          i18n.t(`${localeSelector}.fields.${fieldName}`),
        ),
        null,
        i18n.t('flash.actions.duplicate_exception_field_list'),
      );
    } else {
      this.props.setActiveEntity(modelName, defaultEntity({}, routerProps));
    }

    sendOpenEvent(_.get(user, 'email'), { campaignId: this.props.newlyCreatedID });
  }

  UNSAFE_componentWillUpdate(nextProps: Props) {
    const {
      modelName,
      modifyModelToDuplicateFunc,
      allowDuplication,
      entityIDToDuplicate,
      nonDuplicateFields = [],
      defaultEntity = () => ({}),
      localeSelector,
      idField,
      disableOnSuccessLink,
      routerProps,
      views = {},
      user,
      routerProps: { location = {} },
    } = this.props;
    let { webPath } = this.props;
    if (!_.isEmpty(routerProps.routeParams)) {
      _.forEach(routerProps.routeParams, (val, key) => {
        webPath = _.replace(webPath, `:${key}`, val);
      });
    }

    // Set nonDuplicateFields to default values from defaultEntity
    const nextID = _.get(nextProps.entity, nextProps.idField, '');
    if (
      allowDuplication &&
      entityIDToDuplicate &&
      nextProps.entity &&
      (nextID === entityIDToDuplicate ||
        parseInt(nextID, 10) === parseInt(entityIDToDuplicate, 10))
    ) {
      if (_.isFunction(modifyModelToDuplicateFunc)) {
        const newModel = modifyModelToDuplicateFunc(nextProps.entity);
        _.unset(newModel, 'objective');
        this.props.setActiveEntity(modelName, newModel);
      } else {
        const changes = {};
        _.forEach([...nonDuplicateFields, idField], fieldName =>
          _.set(
            changes,
            fieldName,
            _.get(defaultEntity({}, routerProps), fieldName),
          ),
        );
        _.unset(nextProps.entity, 'objective');
        this.props.setActiveEntity(modelName, {
          ...nextProps.entity,
          ...changes,
        });
      }
    }

    if (
      hasJustSuccess(
        this.props.createEntityRequest,
        nextProps.createEntityRequest,
      )
    ) {
      let successPath = '';
      if (views.edit) {
        if (views.show) successPath = `${webPath}/${nextProps.newlyCreatedID}`;
        else if (this.props.hasCountryIDPath) {
          successPath = `${webPath}/${parseInt(
            this.props.entity.countryID,
            10,
          )}/${nextProps.newlyCreatedID}/edit`;
        } else successPath = `${webPath}/${nextProps.newlyCreatedID}/edit`;
        scribe.sendEvent(
          ScribeEventName.CAMPAIGN_CREATED,
          processCampaignData({
            ...nextProps.entity,
            campaignID: nextProps.newlyCreatedID,
            user,
          }),
        );
      }
      if (nextProps.ucdErrMessage) {
        const reason = parseUCDSyncErrMsg(nextProps.ucdErrMessage);
        Toastr.error(`Trident Campaign created successfully, but failed to create in Holos : ${reason}`);
      } else if (disableOnSuccessLink) {
        Toastr.success(
          i18n.t('flash.actions.created', {
            model_name: i18n.t(`models.${localeSelector}`),
          }),
        );
      } else {
        Toastr.success(
          i18n.t('flash.actions.created', {
            model_name: i18n.t(`models.${localeSelector}`),
          }),
          successPath,
        );
      }

      if (this.props.isStayOnCurrentPage) {
        this.context.router.push(successPath);
      } else if (this.props.redirectPath) {
        this.context.router.push(this.props.redirectPath(nextProps.entity));
      } else {
        this.context.router.push(`${webPath}${location.search}`);
      }
    }
  }

  cancel = () => {
    // eslint-disable-next-line no-alert
    const confirmValue = confirm(i18n.t('common.leaving_page_confirmation'));
    if (!confirmValue) return;
    this.props.router.goBack();
  };

  scrollToErrMsg = () => {
    // waiting for 100ms for the UI state to update, otherwise the errMsg div is not rendered in UI yet.
    setTimeout(() => {
      if (!scrollToElement(-65, 'nodeErrMsg')) {
        scrollToElement(-65, 'formErrMsg');
      }
    }, 100);
  };

  create = () => {
    if (!_.isFunction(this.entityForm.isValid) || this.entityForm.isValid()) {
      const { modelName, entity } = this.props;
      this.props.createEntity(modelName, entity);
    } else {
      Toastr.error(i18n.t('errors.form'));
      this.scrollToErrMsg();
    }
  };

  handleEntityChange = (entity: Object) => {
    const newEntity = { ...this.props.entity, ...entity };
    this.props.setActiveEntity(this.props.modelName, newEntity);
  };

  render() {
    const {
      appConfig,
      entity,
      userPermissions,
      localeSelector,
      permissionKey,
      allowActivation,
      entityIDToDuplicate,
      views: { entityForm: EntityForm },
      idField,
      user,
    } = this.props;
    const isModelDuplicated = !!entityIDToDuplicate;
    const isLoading: boolean = isRequestsPending(
      this.props.createEntityRequest,
    );
    const canActivate =
      allowActivation &&
      hasPermission(userPermissions, permissionKey, '*', 'active');

    return (
      <DocumentTitle title={i18n.t(`${localeSelector}.new.title`)}>
        <div>
          <Header as="h2">{i18n.t(`${localeSelector}.new.title`)}</Header>
          <ProgressiveContainer isLoading={isLoading}>
            {entity && !entity[idField] && (
              <div>
                <EntityForm
                  appConfig={appConfig}
                  setRef={form => (this.entityForm = form)}
                  model={entity}
                  onModelChange={this.handleEntityChange}
                  canActivate={canActivate}
                  userPermissions={userPermissions}
                  user={user}
                  isModelDuplicated={isModelDuplicated}
                />
                <Segment>
                  <Grid columns="equal">
                    <Grid.Column>
                      <Button onClick={this.cancel}>
                        {i18n.t('entities.cancel')}
                      </Button>
                    </Grid.Column>
                    <Grid.Column textAlign="right">
                      <Button
                        className="entity-create-btn"
                        primary
                        onClick={this.create}
                      >
                        {i18n.t('entities.create')}
                      </Button>
                    </Grid.Column>
                  </Grid>
                </Segment>
              </div>
            )}
          </ProgressiveContainer>
        </div>
      </DocumentTitle>
    );
  }
}

function mapStateToProps(state: StoreState, ownProps: Object) {
  const {
    modelName,
    stateKey,
    defaultEntity,
    idField,
    allowActivation,
    allowDuplication,
    nonDuplicateFields,
    permissionKey,
    webPath,
    apiPath,
    localeSelector,
    views,
    hasCountryIDPath,
    isStayOnCurrentPage,
    modifyModelToDuplicateFunc,
    params = {},
    location,
    routeParams,
    disableOnSuccessLink,
  } = ownProps;
  const entityState = _.get(state, stateKey, {});

  return {
    appConfig: state.app.config,
    entityID: params.entityID,
    modelName,
    stateKey,
    defaultEntity,
    idField,
    allowActivation,
    allowDuplication,
    nonDuplicateFields,
    permissionKey,
    webPath,
    apiPath,
    localeSelector,
    disableOnSuccessLink,
    views,
    hasCountryIDPath,
    modifyModelToDuplicateFunc,
    isStayOnCurrentPage,
    routerProps: { params, location, routeParams },
    entityIDToDuplicate: ownProps.location.query.entityIDToDuplicate,
    newlyCreatedID: entityState.newlyCreatedID,
    entity: entityState.active,
    userPermissions: state.app.user.userPermissions,
    user: state.app.user,
    createEntityRequest: _.get(
      state.requests,
      `createEntity${_.upperFirst(stateKey)}Request`,
      {},
    ),
    ucdErrMessage: _.get(state, 'newTridents.ucdErrMessage', ''),
  };
}

const ConnectedNewPage = connect(mapStateToProps, {
  getEntity,
  setActiveEntity,
  createEntity,
})(NewPage);

// Exporting named connected component so that root index does not need to reference
// the import name to fix circular dependencies
export { ConnectedNewPage as NewPage };
